import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { QuicksightConsoleRole } from '@app/data-and-reporting/services/quicksight-dashboard.service';

// Response shape for the User Management registration-status lookup (DIS-1706).
export interface QuicksightRegistrationStatusResponse {
  email: string;
  role: QuicksightConsoleRole;
}

// Response shape for the User Management READER-registration action (DIS-1706).
// 'registered' means the user was fully registered in this call. 'provisioning' means the
// client's QuickSight namespace wasn't ready yet -- auto-provisioning has been kicked off (or
// is already in flight) on the backend and the caller is expected to retry this same request
// shortly to complete registration once the namespace is ready (rn-portal PR #512 / DIS-1731).
export interface QuicksightRegisterReaderResponse {
  status: 'registered' | 'provisioning';
  email: string;
  clientId: string;
  userArn?: string;
  userName?: string;
  role?: QuicksightConsoleRole;
}

// Admin-only QuickSight calls used by the User Management "Register as QuickSight READER"
// toggle (DIS-1706) — distinct from QuicksightDashboardService, which handles the self-service
// dashboard/console-access flow for the logged-in user's own client.
@Injectable({
  providedIn: 'root',
})
export class QuicksightAdminService {
  constructor(private http: HttpClient) {}

  getRegistrationStatusUrl(clientId: string, email: string): string {
    return `${environment.portalURLBase}/client/${clientId}/api/quicksight/users/${encodeURIComponent(email)}`;
  }

  registerReaderUrl(clientId: string, email: string): string {
    return `${environment.portalURLBase}/client/${clientId}/quicksight/register/${encodeURIComponent(email)}`;
  }

  // clientId is the target user's client (not necessarily the acting admin's own client) — the
  // backend resolves and returns the real portal user by email regardless of the URL clientId.
  getRegistrationStatus(
    clientId: string,
    email: string,
  ): Observable<QuicksightRegistrationStatusResponse> {
    return this.http
      .get<QuicksightRegistrationStatusResponse>(
        this.getRegistrationStatusUrl(clientId, email),
        { withCredentials: true },
      )
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }

  // The backend independently enforces relay_admin/relay-super-admin and target-client access —
  // this is not a self-service call and always registers READER, never AUTHOR.
  registerReader(
    clientId: string,
    email: string,
  ): Observable<QuicksightRegisterReaderResponse> {
    return this.http
      .put<QuicksightRegisterReaderResponse>(
        this.registerReaderUrl(clientId, email),
        {},
        { withCredentials: true },
      )
      .pipe(
        take(1),
        catchError((err) => throwError(() => err)),
      );
  }
}
